import { Table, Tag } from "antd";
import { idToObjectMap, populateKey } from "common/utils";
import { useMemo } from "react";

export function SettlementAndPaymentRulesetTable({
  rules,
  productOfferings,
  paymentRules,
  settlementRules,
  workflowStates,
}) {
  const productOfferingsById = useMemo(() => {
    return idToObjectMap(productOfferings);
  }, [productOfferings]);

  const workflowStatesById = useMemo(() => {
    return idToObjectMap(workflowStates);
  }, [workflowStates]);

  const columns = [
    {
      dataIndex: "paymentRuleId",
      key: "paymentRuleId",
      render: (index, rule) =>
        rule.paymentRuleId
          ? paymentRules?.find((paymentRule) => paymentRule.id === rule.paymentRuleId)?.name ??
            `Payment Rule #${rule.paymentRuleId}`
          : "-",
      editable: true,
      inputType: "select",
      title: "Payment Rule",
      options: paymentRules?.map((paymentRule) => ({
        label: paymentRule.name,
        value: paymentRule.id,
      })),
    },
    {
      dataIndex: "settlementRuleId",
      key: "settlementRuleId",
      render: (index, rule) =>
        rule.settlementRuleId
          ? settlementRules?.find((settlementRule) => settlementRule.id === rule.settlementRuleId)
              ?.name ?? `Settlement Plan #${rule.settlementRuleId}`
          : "-",
      editable: true,
      inputType: "select",
      title: "Settlement Plan",
      options: settlementRules?.map((settlementRule) => ({
        label: settlementRule.name,
        value: settlementRule.id,
      })),
    },
    {
      title: "Account Status",
      dataIndex: "workflowStateIds",
      key: "workflowStateIds",
      render: (index, rule) => {
        const matchedStates =
          rule.workflowStateIds?.map((id) => workflowStatesById[id]).filter(Boolean) ?? [];
        if (matchedStates.length === 0) return "-";
        return matchedStates.map((state) => (
          <Tag key={`rule-${rule?.id}-workflow-state-${state.id}`}>
            {state.code} - {state.name}
          </Tag>
        ));
      },
    },
    {
      title: "Legal Status",
      dataIndex: "hasLawsuit",
      key: "hasLawsuit",
      render: (index, rule) =>
        ({
          true: <Tag color="red">Contains Legal</Tag>,
          false: <Tag color="green">No Legal</Tag>,
          null: "-",
        }[rule.hasLawsuit]),
    },
    {
      title: "Product",
      dataIndex: "productOfferingId",
      key: "productOfferingId",
      render: (index, rule) => productOfferingsById[rule.productOfferingId]?.name ?? "-",
    },
    {
      title: "Account Balance",
      children: [
        {
          title: "From",
          dataIndex: "minAccountBalance",
          key: "minAccountBalance",
          render: (index, rule) => (rule.minAccountBalance ? `$${rule.minAccountBalance}` : "-"),
        },
        {
          title: "To",
          dataIndex: "maxAccountBalance",
          key: "maxAccountBalance",
          render: (index, rule) => (rule.maxAccountBalance ? `$${rule.maxAccountBalance}` : "-"),
        },
      ],
      key: "dollarAmount",
    },
    {
      title: "Days Since Service Date",
      children: [
        {
          title: "From",
          dataIndex: "minAccountServiceAgeDays",
          key: "minAccountServiceAgeDays",
          render: (index, rule) => rule?.minAccountServiceAgeDays ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxAccountServiceAgeDays",
          key: "maxAccountServiceAgeDays",
          render: (index, rule) => rule?.maxAccountServiceAgeDays ?? "-",
        },
      ],
      key: "daysSinceServiceDate",
    },
    {
      title: "Days in Aktos",
      children: [
        {
          title: "From",
          dataIndex: "minAccountAgeDays",
          key: "minAccountAgeDays",
          render: (index, rule) => rule?.minAccountAgeDays ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxAccountAgeDays",
          key: "maxAccountAgeDays",
          render: (index, rule) => rule?.maxAccountAgeDays ?? "-",
        },
      ],
      key: "daysInAktos",
    },
    {
      title: "Days Since Upload",
      children: [
        {
          title: "From",
          dataIndex: "minAccountUploadAgeDays",
          key: "minAccountUploadAgeDays",
          render: (index, rule) => rule?.minAccountUploadAgeDays ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxAccountUploadAgeDays",
          key: "maxAccountUploadAgeDays",
          render: (index, rule) => rule?.maxAccountUploadAgeDays ?? "-",
        },
      ],
      key: "daysSinceUpload",
    },
    {
      title: "Days Since First Delinquency",
      children: [
        {
          title: "From",
          dataIndex: "minFirstDelinquencyAgeDays",
          key: "minFirstDelinquencyAgeDays",
          render: (index, rule) => rule?.minFirstDelinquencyAgeDays ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxFirstDelinquencyAgeDays",
          key: "maxFirstDelinquencyAgeDays",
          render: (index, rule) => rule?.maxFirstDelinquencyAgeDays ?? "-",
        },
      ],
      key: "daysSinceFirstDelinquency",
    },
    {
      title: "Date of First Delinquency",
      children: [
        {
          title: "From",
          dataIndex: "minDateOfFirstDelinquency",
          key: "minDateOfFirstDelinquency",
          render: (index, rule) => rule?.minDateOfFirstDelinquency ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxDateOfFirstDelinquency",
          key: "maxDateOfFirstDelinquency",
          render: (index, rule) => rule?.maxDateOfFirstDelinquency ?? "-",
        },
      ],
      key: "dateOfFirstDelinquency",
    },
    {
      title: "Turnover Date",
      children: [
        {
          title: "From",
          dataIndex: "minTurnoverDate",
          key: "minTurnoverDate",
          render: (index, rule) => rule?.minTurnoverDate ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxTurnoverDate",
          key: "maxTurnoverDate",
          render: (index, rule) => rule?.maxTurnoverDate ?? "-",
        },
      ],
      key: "turnoverDate",
    },
    {
      title: "Days Since Turnover",
      children: [
        {
          title: "From",
          dataIndex: "minTurnoverAgeDays",
          key: "minTurnoverAgeDays",
          render: (index, rule) => rule?.minTurnoverAgeDays ?? "-",
        },
        {
          title: "To",
          dataIndex: "maxTurnoverAgeDays",
          key: "maxTurnoverAgeDays",
          render: (index, rule) => rule?.maxTurnoverAgeDays ?? "-",
        },
      ],
      key: "daysSinceTurnover",
    },
  ];
  return <Table bordered columns={columns} dataSource={populateKey(rules)} pagination={false} />;
}
